<script setup>
import { computed } from 'vue';
import AchievementTable from '@/components/result/AchievementTable.vue'; 

const props = defineProps({
  pagetype: {
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value)
  }, 
  region: {type: String, default: ''},
  studentName: { type: String, required: true },
  grade: { type: [String, Number], required: true },
  scoreRangeMin: { type: Number, required: true },
  scoreRangeMax: { type: Number, required: true },
  studentScore: { type: Number, required: true },
  averageScore: { type: Number, required: true },
  levels: {
    type: Array,
    required: true,   
  },
  subAreaData: {
    type: Object,
    default: () => ({})
  }
});

const formattedGrade = computed(() => `${props.grade}학년`);

const cardImageSrc = computed(() => {
  if (props.pagetype === 'literacy') {
    return literacyCardImage;
  }
  if (props.pagetype === 'numeracy') {
    return numeracyCardImage; 
  }
  return ''; 
});

const cardTitlePrefix = computed(() => {
  return props.pagetype === 'literacy' ? '문해력' : '수리력';
});

// 각 레벨의 시작 점수와 끝 점수를 파싱하는 computed
const parsedLevels = computed(() => {
  return props.levels.map(lvl => {
    const [minStr, maxStr] = lvl.range.split('~').map(s => parseInt(s.trim(), 10));

    // 각 levelItem에 평균 점수가 해당 범위에 속하는지 여부 추가
    const isAverageScoreInThisLevel = (
      props.averageScore >= minStr && props.averageScore <= maxStr
    );

    return {
      ...lvl,
      rangeMin: isNaN(minStr) ? 0 : minStr,
      rangeMax: isNaN(maxStr) ? 0 : maxStr,
      isAverageScoreInThisLevel: isAverageScoreInThisLevel
    };
  });
});

// 학생 점수가 어떤 레벨에 속하는지 찾아내는 computed
const studentLevelByScore = computed(() => {
  return parsedLevels.value.find(lvl => {
    return props.studentScore >= lvl.rangeMin && props.studentScore <= lvl.rangeMax;
  }) || null;
});
</script>

<template>
  <div>
    <div class="card pt-5">
      <!--차트로 대체 -->
      <div class="level-chart" :class="[props.pagetype, props.region]">         
        <ul>           
          <li v-for="levelItem in parsedLevels" :key="levelItem.level"
              :class="{ 'is-active': levelItem.level === studentLevelByScore?.level }">
                <div v-if="levelItem.level === studentLevelByScore?.level" class="my-info">
                  <div class="my-level">
                    <p>나의 수준</p>
                    <p>{{ studentLevelByScore?.level }}</p>
                  </div>
                  <div class="my-score">
                    <p>나의 점수</p>
                    <p>{{ props.studentScore }}</p>
                  </div>                  
              </div>
            <div class="level-wrap"> 
              <p>{{ levelItem.level }}수준</p>             
              <p :class="{ 'hidden-text': !props.studentScore }">{{ levelItem.rangeMin }} ~ {{ levelItem.rangeMax }}</p>     
            </div>                  
            <div v-if="levelItem.isAverageScoreInThisLevel && props.studentScore" class="average-info">                
                <p class="average-label">평균&nbsp;점수</p>
                <p class="average-value">{{ props.averageScore }}</p>               
            </div>
          </li>
        </ul>        
      </div>      
      <ul v-if="props.studentScore" class="info pt-4 pb-8">
        <li> {{ formattedGrade }} {{ cardTitlePrefix }} 점수 범위는 <strong class="font-medium">{{ props.scoreRangeMin }}~{{ props.scoreRangeMax }}</strong>점입니다.</li>
        <li><span :class="`${props.pagetype}-color`">{{ props.studentName }}</span> 학생의 {{ cardTitlePrefix }} 점수는 <span :class="`${props.pagetype}-color`">{{ props.studentScore }}</span>점으로 {{ cardTitlePrefix }} 수준은 <span :class="`${props.pagetype}-color`">{{ studentLevelByScore?.level }}</span>수준에 해당합니다.</li>
        <li>본 진단검사에 표집학교로 참여한 <strong class="font-medium">{{ formattedGrade }} 학생 전체의 평균 점수</strong>는 <span :class="`${props.pagetype}-color`">{{ props.averageScore }}</span>점입니다.</li>
      </ul>
      <div v-else class="info-spacer"></div>
    </div>    
  </div>
</template>

<style lang="scss" scoped>
.info li {
    position: relative;
    padding-left: 30px;
    font-size: 16px;
    line-height: 1.5;
    &::after {
        content: '';
        position: absolute;
        width: 3px;
        height: 3px;
        top: 50%;
        left: 15px;
        transform: translateY(-50%);
        border-radius: 100%;
        background-color: #000 !important;
    }
}

.hidden-text {
    color: white !important;
}

.info-spacer {
    height: 80px;
}
.level-chart {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: #FAFAFA;
  border-radius: 10px;
  ul {
    display: flex;    
    width: 880px;
    padding-top: 80px;
    li {
      width: 25%;
      text-align: center;   
      position: relative;
      .level-wrap {
        border: 2px solid #000;        
        border-left: 0;
        border-right: 0;
        padding: 16px 40px;
        position: relative;
        font-size: 20px;       
        P:nth-child(1) {padding-bottom: 5px; font-weight: 600;}
        p:nth-child(2) {
          background-color: #fff;
          border-radius: 50px;
          border: 1px solid #ccc;
          font-weight: 500;
          padding: 2px 0;
          font-size: inherit;
        }
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 200px;
          background-color: #ddd;
          background-image: url("@/assets/images/result/line_vertical.png");
          background-size: contain;
          background-repeat: no-repeat;
        }
      }
      .my-info {
        position: absolute;
        top: -115px;
        left: 50%;
        transform: translateX(-50%);
        width: 90%;
        // height: 90px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 8px 10px 10px 10px;
        border-radius: 10px;
        color: #fff;
        font-size: 18px;
        .my-level, .my-score {width: calc((100% - 10px) / 2);}
        p:nth-child(1) {padding-bottom: 5px;}
        p:nth-child(2) {
          background-color: #fff;
          border-radius: 5px;
          color: #000 !important;
          font-weight: 700 !important;
          padding: 4px 0;
          font-size: 24px !important;
          white-space: nowrap;
        }
        &::after {
          content:'';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 11px 11px 0px 11px;         
        }
      }
      .average-info {
        position: relative;
        top: 25px;
        left: 50%;
        transform: translateX(-50%);
        border: 2px solid ;
        border-radius: 20px;
        padding: 10px;      
        width: 50%;
        background-color: #fff;
        .average-label {
          font-weight: 500 !important;
          padding-bottom: 5px;
          white-space: nowrap;
          color: inherit;
        }
        .average-value {
          font-size: 24px !important;
          font-weight: 500 !important;
          border-radius: 10px;
          padding: 4px 0;
          color: #000 !important;
          white-space: nowrap;
        }
        &::after {
          content:'';
          position: absolute;
          top: -11px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0px 11px 11px 11px;     
        }
      }
      &:nth-child(1) {
        .level-wrap {
          border-top-left-radius: 8px ;
          border-bottom-left-radius: 8px ;
          border-left: 2px solid #000;   
          &::before {
            display: none;
          }     
        }
      }      
      &:nth-child(4) {
        .level-wrap {
          border-top-right-radius: 8px ;
          border-bottom-right-radius: 8px ;
          border-right: 2px solid #000;    
        }
      }   
    }
  }
  &.literacy {
    li:nth-child(1) .level-wrap {
      background-color: rgba(0, 91, 172, 0.05);
    }
    li:nth-child(2) .level-wrap {
      background-color: rgba(0, 91, 172, 0.1);
    }
    li:nth-child(3) .level-wrap {
      background-color: rgba(0, 91, 172, 0.2);
    }
    li:nth-child(4) .level-wrap {
      background-color: rgba(0, 91, 172, 0.3);
    }
    li .level-wrap {
      p:nth-child(2) {border-color: rgba(0, 91, 172, 0.3);}
    }
    .my-info {
      background-color: #005BAC !important;
      &::after {
        border-color: #005BAC transparent transparent transparent;
      }
    }
    .average-info {
      border-color: #005BAC !important;
      .average-label {
        color: #005BAC !important;
      }
      .average-value {
        background-color: #F7F9FE !important;
      }
      &::after {
        border-color: transparent transparent #005BAC transparent;
      }
    }
  }
  &.numeracy {
    li:nth-child(1) .level-wrap {
      background-color: rgba(206, 78, 86, 0.05);
    }
    li:nth-child(2) .level-wrap {
      background-color: rgba(206, 78, 86, 0.1);
    }
    li:nth-child(3) .level-wrap {
      background-color: rgba(206, 78, 86, 0.2);
    }
    li:nth-child(4) .level-wrap {
      background-color: rgba(206, 78, 86, 0.3);
    }
    li .level-wrap {
      p:nth-child(2) {border-color: rgba(206, 78, 86, 0.3);}
    }
    .my-info {
      background-color: #CE4E56 !important;
      &::after {
        border-color: #CE4E56 transparent transparent transparent;
      }
    }
    .average-info {
      border-color: #CE4E56 !important;
      .average-label {
        color: #CE4E56 !important;
      }
      .average-value {
        background-color: #FEF8F9 !important;
      }
      &::after {
        border-color: transparent transparent #CE4E56 transparent;
      }
    }
  }
  &.seoul {
    ul li .my-info::before {
      content: '';
      position: absolute;
      top: 0;
      left: -75px;
      width: 67px;
      height: 70px;
      background-image: url("@/assets/images/result/char_03.png");
      background-size: contain;
      background-repeat: no-repeat;
    }
    ul li .average-info::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: -82px;
      width: 70px;
      height: 70px;
      background-image: url("@/assets/images/result/char_04.png");
      background-size: contain;
      background-repeat: no-repeat;
    }
  } 
}
</style>